import React from 'react';
import {
  Card,
  Pagination,
  PageSection,
  Title,
  Toolbar,
  ToolbarContent,
  ToolbarItem,
  Label,
  PaginationVariant
} from '@patternfly/react-core';
import { Table, Thead, Tr, Th, Tbody, Td } from '@patternfly/react-table';
import { assetFindingsRows, assetFindingsColumns, highPriorityThresh, lowPriorityThresh } from '@app/Findings/sampleData';
import { ChartDonut } from '@patternfly/react-charts';
import { PRODUCT_NAME } from '../../config/partner';

export const assetFindings: React.FunctionComponent = () => {
  const [page, setPage] = React.useState<number>(1);
  const [perPage, setPerPage] = React.useState<number>(10);
  const [paginatedRows, setPaginatedRows] = React.useState(assetFindingsRows.slice(0, 10));

  const handleSetPage = (
    _evt: React.MouseEvent | React.KeyboardEvent | MouseEvent,
    newPage: number,
    _perPage: number,
    startIdx: number,
    endIdx: number
  ) => {
    setPaginatedRows(assetFindingsRows.slice(startIdx, endIdx));
    setPage(newPage);
  };

  const handlePerPageSelect = (
    _evt: React.MouseEvent | React.KeyboardEvent | MouseEvent,
    newPerPage: number,
    newPage: number,
    startIdx: number,
    endIdx: number
  ) => {
    setPaginatedRows(assetFindingsRows.slice(startIdx, endIdx));
    setPage(newPage);
    setPerPage(newPerPage);
  };

  const renderPagination = (variant: string, isCompact: boolean) => (
    <Pagination
      isCompact={isCompact}
      itemCount={assetFindingsRows.length}
      page={page}
      perPage={perPage}
      onSetPage={handleSetPage}
      onPerPageSelect={handlePerPageSelect}
      variant={variant as PaginationVariant}
      titles={{
        paginationAriaLabel: `${variant} pagination`
      }}
    />
  );

  const tableToolbar = (
    <Toolbar usePageInsets id="compact-toolbar">
      <ToolbarContent>
        <ToolbarItem variant="pagination">{renderPagination('top', true)}</ToolbarItem>
      </ToolbarContent>
    </Toolbar>
  );

  const renderLabel = (labelText: string) => {
    switch (labelText) {
      case 'Running':
        return <Label color="green">{labelText}</Label>;
      case 'Stopped':
        return <Label color="orange">{labelText}</Label>;
      case 'Needs Maintenance':
        return <Label color="blue">{labelText}</Label>;
      case 'Down':
        return <Label color="red">{labelText}</Label>;
      default:
        return <></>;
    }
  };

  const renderPriority = (priority: number) => {
    if (priority > highPriorityThresh) {
        return <Label color="red">{priority}</Label>;
    } else if (priority >= lowPriorityThresh) {
        return <Label color="orange">{priority}</Label>;
    } else {
        return <Label color="green">{priority}</Label>;
    }
  };

  const highCount = assetFindingsRows.filter((obj) => obj.Priority > highPriorityThresh).length;
  const mediumCount = assetFindingsRows.filter((obj) => obj.Priority <= highPriorityThresh).filter((obj) => obj.Priority >= lowPriorityThresh).length;
  const lowCount = assetFindingsRows.filter((obj) => obj.Priority < lowPriorityThresh).length;

  return (
    <React.Fragment>
        <PageSection isFilled>
          <Title headingLevel="h1" size="xl">{PRODUCT_NAME} Asset Findings</Title>
          <br />
          <Card>
            {tableToolbar}
            <Table variant="compact" aria-label="Compact Table">
              <Thead>
                <Tr>
                  <Th key={0}>{assetFindingsColumns[0]}</Th>
                  <Th key={1}>{assetFindingsColumns[1]}</Th>
                  <Th key={2}>{assetFindingsColumns[2]}</Th>
                  <Th key={3}>{assetFindingsColumns[3]}</Th>
                  <Th key={4}>{assetFindingsColumns[4]}</Th>
                  <Th key={5}>{assetFindingsColumns[5]}</Th>
                  <Th key={6}>{assetFindingsColumns[6]}</Th>
                </Tr>
              </Thead>
              <Tbody>
                {paginatedRows.map((row, rowIndex) => (
                  <Tr key={rowIndex}>
                    <>
                      <Td dataLabel={assetFindingsColumns[0]}>{row.Source}</Td>
                      <Td dataLabel={assetFindingsColumns[1]}>{renderPriority(row.Priority)}</Td>
                      <Td dataLabel={assetFindingsColumns[2]}>{row.FindingName}</Td>
                      <Td dataLabel={assetFindingsColumns[3]}>{row.Resource}</Td>
                      <Td dataLabel={assetFindingsColumns[4]}>{row.Category}</Td>
                      <Td dataLabel={assetFindingsColumns[5]}>{row.Description}</Td>
                      <Td dataLabel={assetFindingsColumns[6]}>{row.SuggestedRemediation}</Td>
                    </>
                  </Tr>
                ))}
              </Tbody>
            </Table>
          </Card>
        </PageSection>

        <div style={{ height: '230px', width: '450px' }}>
          <ChartDonut
            ariaDesc="Asset findings by priority"
            ariaTitle="Asset findings by priority"
            data={[{ x: 'High', y: highCount }, { x: 'Medium', y: mediumCount }, { x: 'Low', y: lowCount }]}
            labels={({ datum }) => `${datum.x}: ${datum.y}`}
            style={{
                data: {
                    fill: ({ datum }) => {
                        if (datum.x === 'High') {
                            return 'red';
                        } else if (datum.x === 'Medium') {
                            return 'orange';
                        } else if (datum.x === 'Low') {
                            return 'green';
                        }
                        return null;
                    }
                }
            }}
            legendData={[{ name: 'High Priority: '+highCount, symbol: { fill: 'red' }}, { name: 'Medium Priority: '+mediumCount, symbol: { fill: 'orange' }}, { name: 'Low Priority: '+lowCount, symbol: { fill: 'green' }}]}
            legendOrientation="vertical"
            legendPosition="right"
            name="chart1"
             padding={{
               bottom: 20,
               left: 50,
               right: 200, // Adjusted to accommodate legend
               top: 20
             }}
            title={assetFindingsRows.length}
            subTitle="Asset Findings"
            width={450}
            innerRadius={80}
            constrainToVisibleArea
          />
        </div>
    </React.Fragment>
  );
};
